import { memo, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { spacing } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

const ConfirmModal = memo(({ isOpen, title = 'Confirm', description = '', onConfirm, onCancel, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger = false }) => {
  const { colors } = useTheme();
  const [cancelHover, setCancelHover] = useState(false);
  const [confirmHover, setConfirmHover] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const buttonBase = {
    height: '36px',
    padding: '0 14px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    letterSpacing: '-0.005em',
    fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    outline: 'none',
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.35)',
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        width: 'min(520px, 92%)',
        background: colors.background,
        borderRadius: '8px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: spacing.xl,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.foreground }}>{title}</div>
            {description && <div style={{ fontSize: '0.875rem', color: colors.foregroundSecondary, marginTop: spacing.xs }}>{description}</div>}
          </div>
          <button
            aria-label="Close"
            onClick={onCancel}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              color: colors.foreground,
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = colors.backgroundOffset}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: `${spacing.md} ${spacing.xl}`, display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            onMouseEnter={() => setCancelHover(true)}
            onMouseLeave={() => setCancelHover(false)}
            style={{
              ...buttonBase,
              background: cancelHover ? colors.backgroundOffset : 'transparent',
              border: `1px solid ${colors.border}`,
              color: colors.foreground,
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            onMouseEnter={() => setConfirmHover(true)}
            onMouseLeave={() => setConfirmHover(false)}
            style={{
              ...buttonBase,
              border: 'none',
              background: danger ? (confirmHover ? '#F5D0D0' : '#FDE8E8') : (confirmHover ? '#5A7A59' : '#6D916C'),
              color: danger ? '#C0392B' : 'white',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
});

export default ConfirmModal;
